import { useState, useEffect } from 'react';
import { Radio, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { API } from '@/App';
import axios from 'axios';

const LiveScoreTicker = ({ transparent = false }) => {
  const [liveMatches, setLiveMatches] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLiveMatches();
    // Refresh live scores every 15 seconds
    const interval = setInterval(fetchLiveMatches, 15000);
    return () => clearInterval(interval);
  }, []);

  // Auto-rotate ticker every 5 seconds
  useEffect(() => {
    if (liveMatches.length > 1) {
      const rotateInterval = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % liveMatches.length);
      }, 5000);
      return () => clearInterval(rotateInterval);
    }
  }, [liveMatches.length]);

  const fetchLiveMatches = async () => {
    try {
      const response = await axios.get(`${API}/matches/live`);
      setLiveMatches(response.data || []);
    } catch (error) {
      console.error('Failed to fetch live matches:', error);
      setLiveMatches([]);
    } finally {
      setLoading(false);
    }
  };

  const formatScore = (match) => {
    const innings = match.innings || [];
    const currentInnings = innings[match.current_innings - 1];
    
    if (!currentInnings) {
      return 'Yet to start';
    }

    const battingTeam = currentInnings.batting_team_id === match.team1_id 
      ? match.team1_name 
      : match.team2_name;
    
    const runs = currentInnings.total_runs || 0;
    const wickets = currentInnings.wickets || 0;
    const overs = currentInnings.overs_completed || 0;
    const balls = currentInnings.balls_in_current_over || 0;

    return `${battingTeam}: ${runs}/${wickets} (${overs}.${balls})`;
  };

  const goToPrevious = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex(prev => (prev - 1 + liveMatches.length) % liveMatches.length);
  };

  const goToNext = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex(prev => (prev + 1) % liveMatches.length);
  };

  if (loading) {
    return (
      <div className={`hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-full ${
        transparent ? 'bg-white/10' : 'bg-gray-100'
      }`}>
        <div className="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
        <span className={`text-xs ${transparent ? 'text-white/70' : 'text-gray-500'}`}>
          Loading...
        </span>
      </div>
    );
  }

  if (liveMatches.length === 0) {
    return (
      <Link
        to="/live"
        className={`hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-full transition-all ${
          transparent 
            ? 'bg-white/10 hover:bg-white/20 text-white/70' 
            : 'bg-gray-100 hover:bg-gray-200 text-gray-500'
        }`}
        data-testid="no-live-matches-ticker"
      >
        <Radio className="w-4 h-4" />
        <span className="text-xs font-medium">No live matches</span>
      </Link>
    );
  }

  const currentMatch = liveMatches[currentIndex];

  return (
    <div 
      className={`hidden lg:flex items-center gap-1 rounded-full overflow-hidden ${
        transparent ? 'bg-white/10' : 'bg-gradient-to-r from-red-50 to-teal-50 border border-red-100'
      }`}
      data-testid="live-score-ticker"
    >
      {/* Live indicator */}
      <div className={`flex items-center gap-1.5 px-2 py-1.5 ${
        transparent ? 'bg-red-500/20' : 'bg-red-500'
      } rounded-l-full`}>
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
        </span>
        <span className={`text-xs font-bold ${transparent ? 'text-red-200' : 'text-white'}`}>
          LIVE
        </span>
      </div>

      {/* Navigation arrows */}
      {liveMatches.length > 1 && (
        <button
          onClick={goToPrevious}
          className={`p-1 transition-colors ${
            transparent ? 'hover:bg-white/10 text-white/70' : 'hover:bg-gray-200 text-gray-500'
          }`}
        >
          <ChevronLeft className="w-3 h-3" />
        </button>
      )}

      {/* Match info */}
      <Link
        to={`/live/${currentMatch.id}`}
        className={`flex items-center gap-2 px-2 py-1 min-w-[180px] max-w-[250px] transition-colors ${
          transparent ? 'hover:bg-white/10' : 'hover:bg-white'
        }`}
      >
        <div className="flex-1 text-center">
          <p className={`text-xs font-semibold truncate ${
            transparent ? 'text-white' : 'text-gray-800'
          }`}>
            {currentMatch.team1_name?.substring(0, 10)} vs {currentMatch.team2_name?.substring(0, 10)}
          </p>
          <p className={`text-xs font-medium ${
            transparent ? 'text-teal-200' : 'text-teal-600'
          }`}>
            {formatScore(currentMatch)}
          </p>
        </div>
      </Link>

      {/* Navigation arrows */}
      {liveMatches.length > 1 && (
        <button
          onClick={goToNext}
          className={`p-1 transition-colors ${
            transparent ? 'hover:bg-white/10 text-white/70' : 'hover:bg-gray-200 text-gray-500'
          }`}
        >
          <ChevronRight className="w-3 h-3" />
        </button>
      )}

      {/* Match count indicator */}
      {liveMatches.length > 1 && (
        <div className={`pr-2 text-xs ${transparent ? 'text-white/50' : 'text-gray-400'}`}>
          {currentIndex + 1}/{liveMatches.length}
        </div>
      )}
    </div>
  );
};

export default LiveScoreTicker;
